import { Alert, Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { formatQty, shortfallAgainstFill } from '../utils/bulkLotNdcStatus';
import { warningTextTone, warningTint } from '../utils/warningTone';

/**
 * POM-6121 — the picked lot cannot cover the fill, stated in the operator's own numbers.
 *
 * Tells, does not ask. This carried a confirmation checkbox until POM-6119: on a bulk screen that
 * meant a tick per row, which is the kind of prompt operators learn to clear without reading — and
 * it proved nothing, because it never reached the server. What it gated was the Apply button, not
 * the payload. The claim is visible either way: it lands in the unverified bucket and an inventory
 * user still has to reconcile it against a receiving record.
 *
 * A hand-recorded lot still asks, and deliberately: that asserts stock inventory has never seen at
 * all, which is a larger claim than a quantity being short.
 *
 * Amber, not an error: nothing is wrong. What is recorded is a claim against stock inventory has
 * no receipt for yet.
 *
 * Its own file because ChangeNdcLotDialog is at the 450-line cap — the same split its sibling
 * panels already use.
 */
const ChangeNdcLotShortfallNotice = ({ lot, fillQuantity }) => (
  <Alert
    severity='warning'
    icon={false}
    sx={{
      mt: 2,
      bgcolor: (theme) => warningTint(theme, 0.12),
      color: warningTextTone,
      border: '1px solid',
      borderColor: (theme) => warningTint(theme, 0.4),
    }}
  >
    <Typography variant='body2' sx={{ color: warningTextTone }}>
      Lot{' '}
      <Box component='span' sx={{ fontFamily: 'monospace' }}>
        {lot?.lotNumber}
      </Box>
      {` has ${formatQty(lot?.remainingQty)} available. The remaining `}
      <Box component='span' fontWeight={700}>
        {`${formatQty(shortfallAgainstFill({ lot, fillQuantity }))} recorded as ${SYSTEM_DEFICIT.toLowerCase()}`}
      </Box>
      {` — a claim against stock that inventory has not received yet. An inventory user verifies
        it when the receiving record lands.`}
    </Typography>
  </Alert>
);

ChangeNdcLotShortfallNotice.propTypes = {
  lot: PropTypes.shape({
    lotNumber: PropTypes.string,
    remainingQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ChangeNdcLotShortfallNotice.defaultProps = {
  lot: null,
  fillQuantity: 0,
};

export default ChangeNdcLotShortfallNotice;
